import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from '@flexibilitypay/ui';
import { AuthApiError, authApi } from '@flexibilitypay/shared';

export interface ChangePasswordDialogProps {
  open: boolean;
  username: string;
  initialCurrentPassword?: string;
  onClose: () => void;
  onSuccess: (args: { username: string; newPassword: string }) => void;
}

/**
 * Password change dialog
 *
 * @taskId HOUSEKEEPING
 * @see Code quality - ESLint fix (derived state)
 */
export function ChangePasswordDialog({
  open,
  username,
  initialCurrentPassword = '',
  onClose,
  onSuccess,
}: ChangePasswordDialogProps) {
  const { t } = useTranslation(['auth', 'common']);
  const ref = useRef<HTMLDialogElement | null>(null);

  const [currentPassword, setCurrentPassword] = useState(initialCurrentPassword);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentPassword(initialCurrentPassword);
      setNewPassword('');
      setConfirmNewPassword('');
      setError(null);
      setSubmitting(false);
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmNewPassword.trim();

    if (trimmedNew !== trimmedConfirm) {
      setError(t('auth:changePassword.errors.passwordsDoNotMatch'));
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changePassword({
        username,
        currentPassword: trimmedCurrent,
        newPassword: trimmedNew,
      });
      onSuccess({ username, newPassword: trimmedNew });
    } catch (err) {
      const appError = err instanceof AuthApiError ? err.appError : undefined;
      if (appError?.httpStatus === 400 && appError.code === 'VALIDATION_ERROR') {
        setError(t('auth:changePassword.errors.policyViolation'));
      } else if (appError?.httpStatus === 401) {
        setError(t('auth:changePassword.errors.invalidCurrent'));
      } else if (appError?.httpStatus === 422 && appError.code === 'AUTH_005') {
        setError(t('auth:changePassword.errors.samePassword'));
      } else {
        setError(t('auth:changePassword.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="backdrop:bg-background/80 fixed top-1/2 left-1/2 m-0 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md rounded-lg border border-border bg-background p-0 text-foreground shadow-lg"
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-base font-semibold">{t('auth:changePassword.title')}</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t('common:actions.close')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
        <p className="text-sm text-muted-foreground">
          {t('auth:changePassword.description')}
        </p>
        <div className="flex flex-col gap-2">
          <Label htmlFor="cp-current">
            {t('auth:changePassword.fields.currentPassword')}
          </Label>
          <Input
            id="cp-current"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="cp-new">{t('auth:changePassword.fields.newPassword')}</Label>
          <Input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="cp-confirm">
            {t('auth:changePassword.fields.confirmNewPassword')}
          </Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={submitting} className="mt-2">
          {submitting
            ? t('auth:changePassword.actions.submitting')
            : t('auth:changePassword.actions.submit')}
        </Button>
      </form>
    </dialog>
  );
}
